import { randomInt } from '../lib/random';

/**
 * Le poisson d'avril.
 *
 * Taper P-O-I-S-S-O-N colle un poisson en papier dans le dos de l'interface :
 * de travers, avec son bout de scotch, à un endroit qu'on n'a pas choisi. Rien
 * ne s'interrompt — c'est la différence avec l'overlay du mot `orange`, qui
 * récompense et bloque. Celui-ci ne dit rien, il colle un poisson et se tait.
 *
 * Les découpes sont mémorisées : elles survivent au rechargement **et au tirage
 * du skin suivant**. On ne laisse pas ses poissons sur une interface, on les
 * laisse sur le site.
 *
 * Tout est pur ici — le hasard entre par un paramètre, jamais par un appel
 * direct — pour que le placement, la borne et la chute se vérifient sans écran.
 */

/**
 * Nombre maximal de poissons collés en même temps.
 *
 * Onze, comme les onze interfaces, et c'est aussi la chute : **le onzième est
 * Steve**. Un visiteur qui reste appuyé sur son clavier n'ira pas plus loin, et
 * décoller une découpe libère une place.
 */
export const MAX_FISH = 11;

/** Nombre de découpes différentes, pour que deux voisins ne soient pas jumeaux. */
export const FISH_CUTS = 4;

/** Inclinaison maximale, en degrés, d'un côté comme de l'autre. */
export const MAX_TILT = 16;

/**
 * Zone de collage, en pourcentage de la fenêtre.
 *
 * Les bords sont écartés : un poisson à cheval sur le bord droit déborderait de
 * la page, et un poisson tout en haut irait se coller sur une barre de menus
 * qu'il faut pouvoir continuer d'utiliser.
 */
export const FISH_AREA = { minX: 7, maxX: 86, minY: 12, maxY: 82 } as const;

/**
 * Écartement visé entre deux découpes, en points de pourcentage.
 *
 * Ce n'est pas une distance en pixels : les deux axes n'ont pas la même échelle.
 * C'est un écartement, et il suffit à éviter l'empilement.
 */
export const MIN_GAP = 18;

/** Essais de placement avant de garder le moins mauvais. */
const PLACEMENT_TRIES = 24;

/** Une découpe, sans son identifiant. */
export interface FishSpot {
  readonly x: number;
  readonly y: number;
  readonly tilt: number;
  readonly cut: number;
  /** Vrai pour la onzième : celle-là, c'est lui. */
  readonly steve: boolean;
}

export interface Fish extends FishSpot {
  readonly id: number;
}

/** Vrai si la collection contient déjà le poisson Steve. */
export function hasSteve(fish: readonly Fish[]): boolean {
  return fish.some((one) => one.steve);
}

/**
 * Où coller la prochaine découpe.
 *
 * Tirage avec rejet : on cherche un point à `MIN_GAP` de tous les autres, et au
 * bout de `PLACEMENT_TRIES` essais on garde celui qui s'en éloignait le plus.
 * Sans ce garde, les dernières découpes finiraient empilées sur les premières.
 */
function placement(
  fish: readonly Fish[],
  pick: (max: number) => number,
): { readonly x: number; readonly y: number } {
  const { minX, maxX, minY, maxY } = FISH_AREA;
  let best: { x: number; y: number } = { x: minX, y: minY };
  let bestGap = -1;

  for (let round = 0; round < PLACEMENT_TRIES; round += 1) {
    const x = minX + pick(maxX - minX + 1);
    const y = minY + pick(maxY - minY + 1);
    const gap = fish.reduce(
      (closest, one) => Math.min(closest, Math.hypot(one.x - x, one.y - y)),
      Number.POSITIVE_INFINITY,
    );
    if (gap >= MIN_GAP) return { x, y };
    if (gap > bestGap) {
      bestGap = gap;
      best = { x, y };
    }
  }
  return best;
}

/**
 * La prochaine découpe, ou `null` quand le mur est plein.
 *
 * La onzième est Steve — sauf s'il est déjà collé quelque part, auquel cas la
 * place qui vient de se libérer est rendue à une découpe ordinaire. Il n'y a
 * jamais deux Steve : il n'y en a qu'un, c'est le principe.
 */
export function nextFish(
  fish: readonly Fish[],
  pick: (max: number) => number = randomInt,
): FishSpot | null {
  if (fish.length >= MAX_FISH) return null;

  const { x, y } = placement(fish, pick);
  return {
    x,
    y,
    tilt: pick(MAX_TILT * 2 + 1) - MAX_TILT,
    cut: pick(FISH_CUTS),
    steve: fish.length + 1 === MAX_FISH && !hasSteve(fish),
  };
}

/** Décolle une découpe. Un identifiant inconnu ne change rien. */
export function peelFish(fish: readonly Fish[], id: number): readonly Fish[] {
  return fish.filter((one) => one.id !== id);
}

/**
 * Forme mémorisée : cinq entiers par découpe, sans accolades ni guillemets.
 *
 * Le JSON aurait demandé la même validation au retour, pour une chaîne trois
 * fois plus longue — et c'est une valeur qui sort de `localStorage`, donc une
 * donnée non fiable dont il faut de toute façon relire chaque champ.
 */
export function serialiseFish(fish: readonly Fish[]): string {
  return fish
    .slice(0, MAX_FISH)
    .map((one) => [one.x, one.y, one.tilt, one.cut, one.steve ? 1 : 0].join(','))
    .join(';');
}

/** Entier attendu dans un intervalle, sinon `null`. */
function intBetween(raw: string | undefined, min: number, max: number): number | null {
  if (raw === undefined) return null;
  const value = Number.parseInt(raw, 10);
  if (!Number.isSafeInteger(value) || value < min || value > max) return null;
  return value;
}

/**
 * Relit la forme mémorisée.
 *
 * Toute découpe douteuse est jetée, pas corrigée : une clé trafiquée ne doit pas
 * pouvoir poser un poisson hors de l'écran, en faire tourner un de 4000 degrés,
 * ni fabriquer un second Steve.
 */
export function parseFish(raw: string | null): readonly Fish[] {
  if (typeof raw !== 'string' || raw === '') return [];

  const out: Fish[] = [];
  let steveSeen = false;

  for (const chunk of raw.split(';')) {
    if (out.length >= MAX_FISH) break;
    const parts = chunk.split(',');
    if (parts.length !== 5) continue;

    const x = intBetween(parts[0], FISH_AREA.minX, FISH_AREA.maxX);
    const y = intBetween(parts[1], FISH_AREA.minY, FISH_AREA.maxY);
    const tilt = intBetween(parts[2], -MAX_TILT, MAX_TILT);
    const cut = intBetween(parts[3], 0, FISH_CUTS - 1);
    const flag = intBetween(parts[4], 0, 1);
    if (x === null || y === null || tilt === null || cut === null || flag === null) continue;

    const steve = flag === 1 && !steveSeen;
    if (steve) steveSeen = true;
    out.push({ id: out.length, x, y, tilt, cut, steve });
  }

  return out;
}
